import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import getCroppedImg from "./utils/cropImage";
import "./Upload-Image.css";
import BottomNav from "../BottomNav";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { notificationService } from '../utils/notificationService';
import { uploadPostImage } from '../utils/imageUpload';

const UploadImageCompetition = () => {
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [saving, setSaving] = useState(false);
  const { state: textState } = useLocation();
  const [title] = useState(textState?.title || "");
  const [tags] = useState(textState?.tags || []);
  const [description] = useState(textState?.description || "");
  const [uid, setUid] = useState(null);
  const [usingFullImage, setUsingFullImage] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const imageUrl = location.state?.imageUrl;

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  useEffect(() => {
    if (imageUrl) {
      setCroppedImage(imageUrl);
    }
  }, [imageUrl]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) =>
      setUid(user ? user.uid : null)
    );
    return unsub;
  }, []);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setShowCropper(true);
    }
  };

  const showCroppedImage = async () => {
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setShowCropper(false);
      setUsingFullImage(false);
      setShowConfirmPopup(true);
    } catch (e) {
      console.error("Cropping failed", e);
    }
  };

  const handlePopupConfirm = async () => {
    setShowConfirmPopup(false);
    await saveImage();
  };

  const saveImage = async () => {
      if (!croppedImage || saving) return;
      setSaving(true);

      try {
        if (!uid) throw new Error("User not authenticated");

        const { filePath } = await uploadPostImage(croppedImage, { uid, folder: "users" });

        navigate("/PreviewCompetitionPage", {
          state: {
            localPreview: croppedImage,
            filePath,
            title,
            tags,
            description,
          }
        });
      } catch (err) {
        console.error("Image upload failed:", err);
        notificationService.showError(err.message || "Upload failed");
        setSaving(false);
      }
    };

  return (
    <div className="upload-page">
      <header className="upload-header">
        <button
          type="button"
          className="upload-back-btn"
          aria-label="Go back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        <h1 className="upload-page-title">Select Image</h1>
      </header>

      {showCropper && (
        <>
          <div className="upload-crop-mat">
            <div className="upload-crop-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
          </div>
          <div className="upload-crop-buttons">
            <button className="upload-crop-btn" onClick={showCroppedImage}>
              Done Cropping
            </button>
            <button
              className="upload-full-btn"
              onClick={() => {
                setCroppedImage(imageSrc);
                setUsingFullImage(true);
                setShowCropper(false);
                setShowConfirmPopup(true);
              }}
            >
              Use Full Image
            </button>
          </div>
        </>
      )}

      {!showCropper && (
        <>
          <div className="upload-photo-slot">
            <label className="sr-only" htmlFor="idea-image-input">Upload an image for your idea</label>
            <input
              id="idea-image-input"
              type="file"
              className="upload-file-input"
              accept="image/*"
              onChange={handleImageChange}
            />

            {croppedImage ? (
              <div
                className="upload-preview-bg"
                style={{ backgroundImage: `url(${croppedImage})` }}
              />
            ) : imageUrl ? (
              <div
                className="upload-preview-bg"
                style={{ backgroundImage: `url(${imageUrl})` }}
              />
            ) : (
              <div className="upload-placeholder">
                <FaUpload size={36} />
                <p>Tap to add a photo</p>
              </div>
            )}
          </div>

          <button
            className="upload-continue-btn"
            onClick={saveImage}
            disabled={saving}
          >
            {saving ? "Uploading…" : "Continue"}
          </button>
        </>
      )}

      {showConfirmPopup && (
        <div className="upload-popup-overlay">
          <div className="upload-note-popup">
            <p>Use this photo for your idea?</p>
            <div className="upload-popup-buttons">
              <button className="upload-popup-ok" onClick={handlePopupConfirm} disabled={saving}>OK</button>
              <button className="upload-popup-cancel" onClick={() => setShowConfirmPopup(false)} disabled={saving}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {saving && (
        <div className="upload-popup-overlay">
          <div className="upload-note-popup">
            <p>Uploading your image…</p>
            <div className="upload-spinner" aria-hidden="true"></div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export default UploadImageCompetition;

